<template>
  <div class="return-details fade-in">
    <!-- Header Section -->
    <div class="return-header card shadow-sm border-0 mb-4">
      <div class="card-body">
        <div class="row align-items-center">
          <div class="col-md-6">
            <h3 class="text-primary mb-2">
              <i class="bi bi-arrow-return-left me-2"></i>Return Details
            </h3>
            <h5 class="text-muted mb-3" v-if="!loading">{{ returnData.store.code }} - {{ returnData.store.name }}</h5>
            <h5 class="mb-2" v-if="!loading">{{ returnData.reference }}</h5>

            <div class="d-flex flex-wrap gap-3 mb-2">
              <div class="sale-info-item">
                <i class="bi bi-person text-primary me-1"></i>
                <span class="text-muted">Created by:</span>
                <span class="fw-medium ms-1" v-if="!loading">{{ returnData.user?.first_name }} {{ returnData.user?.last_name }}</span>
              </div>

              <div class="sale-info-item">
                <i class="bi bi-calendar-date text-primary me-1"></i>
                <span class="text-muted">Created at:</span>
                <span class="fw-medium ms-1" v-if="!loading">{{ formatDate(returnData.created_at) }}</span>
              </div>
            </div>
          </div>

          <div class="col-md-6 text-md-end mt-3 mt-md-0">
            <Badge pill :type="getReturnStatus(returnData.status)" class="status-badge">
              {{ returnData.status }}
            </Badge>

            <div class="mt-3 d-flex flex-wrap justify-content-md-end gap-2">
              <button class="btn btn-outline-secondary" @click="$router.go(-1)">
                <i class="bi bi-arrow-left me-1"></i> Back
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Return Info Card -->
    <Card class="mb-4 shadow-sm border-0">
      <template v-slot:header>
        <div class="d-flex align-items-center">
          <i class="bi bi-info-circle me-2 text-primary"></i>
          <span class="fw-medium">Return Information</span>
        </div>
      </template>
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading return details...</p>
      </div>
      <div v-else class="row">
        <div class="col-md-6 mb-3">
          <h6 class="text-muted mb-2">Sale Reference</h6>
          <p class="mb-0 h5">
            <router-link :to="`/sale/${returnData.sale.id}`" class="text-decoration-none">
              {{ returnData.sale.reference }}
            </router-link>
          </p>
        </div>
        <div class="col-md-6 mb-3">
          <h6 class="text-muted mb-2">Return Reason</h6>
          <p class="mb-0">{{ returnData.reason }}</p>
        </div>
      </div>
    </Card>

    <!-- Products Card -->
    <Card body-classes="p-0" class="mb-5 shadow-sm border-0">
      <template v-slot:header>
        <div class="d-flex align-items-center justify-content-between">
          <div>
            <i class="bi bi-box-seam me-2 text-primary"></i>
            <span class="fw-medium">Returned Products</span>
            <Badge pill type="primary" class="ms-2">{{ returnDetails.length }}</Badge>
          </div>
          <UInput v-model="dataTableSearch" size="md" placeholder="Search products" class="mb-0 search-input"/>
        </div>
      </template>
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading products...</p>
      </div>
      <div v-if="!loading" class="product-table">
        <DataTable show-filter-buttons :data="productData" :headers="dataTableHeaders"
                   :search-keyword="dataTableSearch" :per-page="dataTablePerPage" :current-page="dataTablePage">
          <template v-slot:default="{row}">
            <Cell
                v-for="(cellData, key) in row"
                :key="key"
                class="text-nowrap"
                :cell-classes="[
                    ['status', 'tax'].indexOf(key.toString().toLowerCase()) !== -1?'d-flex justify-content-center':'',
                ]">
              <Badge v-if="key === 'status'" pill type="info">
                {{cellData}}
              </Badge>
              <span v-else>{{cellData}}</span>
            </Cell>
          </template>
        </DataTable>
        <div class="d-flex flex-wrap pt-3 pb-4 align-items-end mx-4 justify-content-between">
          <div class="fw-bold">
            <div class="d-flex flex-wrap gap-3 align-items-center">
              <div class="d-flex align-items-center mb-2">
                <div class="me-3">
                  <span class="badge bg-info rounded-circle p-2 me-2">
                    <i class="bi bi-arrow-return-left"></i>
                  </span>
                  <span>Returned Total:</span>
                </div>
                <h4 class="mb-0 text-info">{{ formatNumber(returnData.total || 0) }}</h4>
              </div>
            </div>
          </div>
          <div class="me-sm-4 mb-sm-0 mb-3">
            <p class="mb-2 small">Rows per page</p>
            <select data="" class="form-select" v-model="dataTablePerPage">
              <option v-for="item in [5, 10, 25, 50, 100]" :key="item" :value="item">{{item}}</option>
            </select>
          </div>
          <div>
            <Pagination v-model="dataTablePage" :pages="Math.ceil(returnDetails.length/dataTablePerPage)" :threshold="2">
              <template v-slot:next>Next</template>
              <template v-slot:previous>Prev</template>
            </Pagination>
          </div>
        </div>
      </div>
    </Card>
  </div>
</template>

<script>
import formatNumber from "@/utils/formatNumber";
import {decrypt} from "@/utils/crypto";
import saleService from "@/api/saleService";
import formatDate from "@/utils/formatDate";
import Badge from "@/components/badge/Badge.vue";
import DataTable from "@/components/datatable/DataTable.vue";
import Cell from "@/components/datatable/Cell.vue";
import Pagination from "@/components/pagination/Pagination.vue";
import UInput from "@/components/input/Input.vue";

export default {
  name: "ReturnDetails",
  components: {
    Badge,
    DataTable,
    Cell,
    Pagination,
    UInput
  },
  directives: {
    print
  },

  data(){
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    return {
      profile: {},
      store: {},
      organization: {},
      returnData: {},
      returnDetails: [],
      loading: true,
      // DataTable properties
      dataTableHeaders: [
        'SKU',
        'Code',
        'Name',
        'Quantity',
        'Price',
        'Tax',
        'Total'
      ],
      dataTableSearch: '',
      dataTablePage: 1,
      dataTablePerPage: 10,
    }
  },

  methods:{
    formatDate,
    formatNumber,
    async getReturnDetails() {
      try {
        this.returnData = await saleService.getReturnDetails(this.$route.params.id);
        this.returnDetails = this.returnData.details;
        this.loading = false;
      } catch (error) {
        console.error('Error loading return details:', error);
        this.loading = false;
      }
    },
    getReturnStatus(status) {
      switch (status) {
        case 'completed':
          return 'success';
        case 'pending':
          return 'warning';
        case 'cancelled':
          return 'danger';
        default:
          return 'secondary';
      }
    }
  },

  computed:{
    productData(){
      return this.returnDetails.map(product => {
        return {
          sku: product.product?.sku,
          code: product.product?.code,
          name: product.product?.name,
          quantity: product.quantity,
          price: this.formatNumber(product.price),
          tax: this.formatNumber(product.tax),
          total: this.formatNumber(product.price * product.quantity),
        }
      })
    }
  },

  async created(){
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    this.store = JSON.parse(decrypt(sessionStorage.getItem('store')))
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    await this.getReturnDetails()
  }
}
</script>

<style scoped>
.status-badge {
  font-size: 0.85rem;
  padding: 0.5rem 0.75rem;
}

.search-input {
  width: 250px;
}

/* ReturnDetails page styles */
.return-details {
  padding: 0.5rem;
}

/* Table styles */
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  padding: 1rem 1.5rem;
  vertical-align: middle;
  border-color: #e9ecef;
  color: #4b5563;
}

/* Animation for content loading */
.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Sale info item styling */
.sale-info-item {
  display: flex;
  align-items: center;
  background-color: #f8f9fa;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.9rem;
}
</style>
